import { useMemo } from 'react';
import type { Application, JobPosition, JobPosting, ApplicationState } from '@/types';
import { isApplicationPipelineRecentlyInFlight } from '@/lib/pendingProcessing';
import {
  computePipelineStatusCounts,
  type PipelineStatusCounts,
} from '@/lib/dashboardApplicationBuckets';
import { isApplicationArchived } from '@/lib/applicationArchive';

export type Period = '7d' | '30d' | '90d' | 'all';

export const TARGET_TTH_DAYS = 20;
export const TARGET_TTF_DAYS = 30;

function daysAgo(days: number): Date {
  const d = new Date();
  d.setDate(d.getDate() - days);
  d.setHours(0, 0, 0, 0);
  return d;
}

function periodToDays(p: Period): number | null {
  if (p === '7d') return 7;
  if (p === '30d') return 30;
  if (p === '90d') return 90;
  return null;
}

function median(arr: number[]): number | null {
  if (arr.length === 0) return null;
  const s = [...arr].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 !== 0 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

function filterByPeriod(apps: Application[], period: Period): Application[] {
  const days = periodToDays(period);
  if (days === null) return apps;
  const cutoff = daysAgo(days);
  return apps.filter(a => new Date(a.appliedAt) >= cutoff);
}

function filterByPrevPeriod(apps: Application[], period: Period): Application[] {
  const days = periodToDays(period);
  if (days === null) return [];
  const cutoffCurrent = daysAgo(days);
  const cutoffPrev = daysAgo(days * 2);
  return apps.filter(a => {
    const d = new Date(a.appliedAt);
    return d >= cutoffPrev && d < cutoffCurrent;
  });
}

function getHireDate(app: Application): Date | null {
  const entry = app.history?.find(h => h.toState === 'HIRED');
  if (entry) return new Date(entry.timestamp);
  if (app.state === 'HIRED') return new Date(app.updatedAt);
  return null;
}

function daysBetween(a: Date, b: Date): number {
  return Math.abs(b.getTime() - a.getTime()) / (1000 * 60 * 60 * 24);
}

const TERMINAL_STATES: ApplicationState[] = ['HIRED', 'REJECTED', 'OFFER_DECLINED', 'WITHDRAWN'];
const BEYOND_PIPELINE: ApplicationState[] = ['AWAITING_DECISION', 'REJECTED', 'HIRED', 'OFFER_DECLINED', 'WITHDRAWN'];

export function useDashboardMetrics(
  applications: Application[],
  positions: JobPosition[],
  postings: JobPosting[],
  period: Period,
  options?: { hasProcessingCredits?: boolean },
) {
  const hasProcessingCredits = options?.hasProcessingCredits;
  return useMemo(() => {
    const periodApps = filterByPeriod(applications, period);
    const prevApps = filterByPrevPeriod(applications, period);
    const activePeriodApps = periodApps.filter((a) => !isApplicationArchived(a));
    const days = periodToDays(period);
    const cutoff = days !== null ? daysAgo(days) : null;
    const now = new Date();

    // Sprint dashboard_alignment_v1 (26/05/26 22:00):
    // Snapshot metrics (open/active/awaiting/processing/aging/pipelineErrors)
    // were previously always all-time and conflicted with the period
    // selector. They are now ALL period-scoped:
    // - positions: include those active that overlap the period (created
    //   before period end AND not closed before period start).
    // - postings: same overlap test.
    // - applications-state snapshots: filtered to applications received
    //   in the selected period only.
    //
    // For period='all' the filter degenerates to identity (cutoff === null).
    const inPeriod = (createdAt: string | number | Date, statusActive: boolean): boolean => {
      if (cutoff === null) return statusActive; // no filter
      const created = new Date(createdAt);
      // Active in period = created on or before now AND was/is active during the window
      return created <= now && (statusActive || created >= cutoff);
    };

    // Hero metrics (all period-scoped)
    const openPositions = positions.filter(p =>
      p.status === 'active' && (cutoff === null ? true : new Date(p.createdAt) <= now)
    ).length;
    const activePostings = postings.filter(p =>
      p.isActive && (cutoff === null ? true : new Date(p.createdAt as unknown as string) <= now)
    ).length;
    const periodAppCount = periodApps.length;
    const prevAppCount = prevApps.length;
    // Snapshot states scoped to applications received in the period (active queue only).
    const awaitingDecision = activePeriodApps.filter(a => a.state === 'AWAITING_DECISION').length;
    const processing = activePeriodApps.filter(a => isApplicationPipelineRecentlyInFlight(a)).length;

    // Aging > 30d — open active positions whose age > 30d as of period end.
    const agingPositions = positions.filter(p =>
      p.status === 'active' && daysBetween(new Date(p.createdAt), now) > 30
    ).length;
    void inPeriod; // helper kept for future expansions; not used directly yet

    // Median Time to Hire
    const hiredInPeriod = periodApps.filter(a => a.state === 'HIRED');
    const tthDays = hiredInPeriod.map(a => {
      const hireDate = getHireDate(a);
      if (!hireDate) return null;
      return daysBetween(new Date(a.appliedAt), hireDate);
    }).filter((d): d is number => d !== null);
    const medianTTH = median(tthDays);

    // Median Time to Fill
    const posMap = new Map(positions.map(p => [p.id, p]));
    const filledPositionIds = new Set<string>();
    const ttfByPosition: Record<string, number> = {};
    hiredInPeriod.forEach(a => {
      if (filledPositionIds.has(a.positionId)) return;
      const pos = posMap.get(a.positionId);
      if (!pos) return;
      const hireDate = getHireDate(a);
      if (!hireDate) return;
      ttfByPosition[a.positionId] = daysBetween(new Date(pos.createdAt), hireDate);
      filledPositionIds.add(a.positionId);
    });
    const medianTTF = median(Object.values(ttfByPosition));

    // OAR proxy
    const hiredCount = periodApps.filter(a => a.state === 'HIRED').length;
    const declinedCount = periodApps.filter(a => a.state === 'OFFER_DECLINED').length;
    const oarDenom = hiredCount + declinedCount;
    const oar = oarDenom > 0 ? hiredCount / oarDenom : null;

    // Pipeline errors — active queue only; archived rows stay in outcome analytics.
    const pipelineErrors = activePeriodApps.filter(a => a.state === 'PIPELINE_ERROR').length;

    // Funnel
    const funnelApplied = periodApps.length;
    const funnelScreened = periodApps.filter(a =>
      a.aiScore != null || (a.screeningReports && a.screeningReports.length > 0) || BEYOND_PIPELINE.includes(a.state)
    ).length;
    const funnelInterview = periodApps.filter(a =>
      a.interviews?.some(i => i.status === 'Completed' || i.status === 'Scheduled')
    ).length;
    const funnelDecision = periodApps.filter(a =>
      a.state === 'AWAITING_DECISION' || a.state === 'OFFER_DECLINED' || a.state === 'HIRED'
    ).length;
    const funnelHired = hiredCount;

    const funnel = [
      { stage: 'Applied', count: funnelApplied },
      { stage: 'Screened', count: funnelScreened },
      { stage: 'Interview', count: funnelInterview },
      { stage: 'Decision', count: funnelDecision },
      { stage: 'Hired', count: funnelHired },
    ];

    // Conversion rates
    const funnelWithRates = funnel.map((f, i) => ({
      ...f,
      rate: i === 0 ? null : (funnelApplied > 0 ? (f.count / funnelApplied) * 100 : null),
    }));

    // Ratios
    const applicantToInterview = funnelApplied > 0 ? (funnelInterview / funnelApplied) * 100 : null;
    const interviewToHire = funnelInterview > 0 ? (funnelHired / funnelInterview) * 100 : null;
    const funnelRejected = periodApps.filter(a => a.state === 'REJECTED').length;
    const applicantToHire = funnelApplied > 0 ? (funnelHired / funnelApplied) * 100 : null;
    const applicantToReject = funnelApplied > 0 ? (funnelRejected / funnelApplied) * 100 : null;

    // Sourcing / channels
    const channelCounts: Record<string, { applied: number; hired: number }> = {};
    periodApps.forEach(a => {
      const ch = a.channel || 'Other';
      if (!channelCounts[ch]) channelCounts[ch] = { applied: 0, hired: 0 };
      channelCounts[ch].applied++;
      if (a.state === 'HIRED') channelCounts[ch].hired++;
    });
    const channelData = Object.entries(channelCounts)
      .map(([channel, data]) => ({
        channel,
        applied: data.applied,
        hired: data.hired,
        hireRate: data.applied >= 5 ? ((data.hired / data.applied) * 100) : null,
      }))
      .sort((a, b) => b.applied - a.applied);

    // Top positions by volume
    const posVolume: Record<string, {
      title: string;
      positionId: string;
      applied: number;
      rejected: number;
      awaiting: number;
      errors: number;
    }> = {};
    periodApps.forEach(a => {
      if (!posVolume[a.positionId]) {
        posVolume[a.positionId] = {
          title: a.positionTitle,
          positionId: a.positionId,
          applied: 0,
          rejected: 0,
          awaiting: 0,
          errors: 0,
        };
      }
      posVolume[a.positionId].applied++;
      if (a.state === 'REJECTED') posVolume[a.positionId].rejected++;
      if (!isApplicationArchived(a) && a.state === 'AWAITING_DECISION') posVolume[a.positionId].awaiting++;
      if (!isApplicationArchived(a) && a.state === 'PIPELINE_ERROR') posVolume[a.positionId].errors++;
    });
    const topPositions = Object.values(posVolume)
      .sort((a, b) => b.applied - a.applied)
      .slice(0, 10)
      .map(p => ({ ...p, rejectRate: p.applied > 0 ? (p.rejected / p.applied) * 100 : 0 }));

    // Time in states (median days per state for closed apps)
    const closedApps = periodApps.filter(a => TERMINAL_STATES.includes(a.state) && a.history?.length > 1);
    const stateTimeAgg: Record<string, number[]> = {};
    closedApps.forEach(a => {
      const hist = [...a.history].sort((x, y) => new Date(x.timestamp).getTime() - new Date(y.timestamp).getTime());
      for (let i = 0; i < hist.length - 1; i++) {
        const state = hist[i].toState;
        const dur = daysBetween(new Date(hist[i].timestamp), new Date(hist[i + 1].timestamp));
        if (!stateTimeAgg[state]) stateTimeAgg[state] = [];
        stateTimeAgg[state].push(dur);
      }
    });
    const timeInStates = Object.entries(stateTimeAgg)
      .map(([state, durations]) => ({ state, medianDays: Math.round((median(durations) || 0) * 10) / 10 }))
      .filter(s => s.medianDays > 0)
      .sort((a, b) => b.medianDays - a.medianDays);

    // Trend (weekly or daily) — `days` already resolved above
    const bucketDays = days && days <= 7 ? 1 : 7;
    const trendBuckets: Record<string, { label: string; applied: number; hired: number; rejected: number }> = {};
    periodApps.forEach(a => {
      const d = new Date(a.appliedAt);
      const bucketDate = new Date(d);
      if (bucketDays === 7) {
        bucketDate.setDate(bucketDate.getDate() - bucketDate.getDay());
      }
      const key = bucketDate.toISOString().split('T')[0];
      if (!trendBuckets[key]) {
        trendBuckets[key] = {
          label: bucketDays === 1
            ? d.toLocaleDateString('en', { month: 'short', day: 'numeric' })
            : `W${Math.ceil(d.getDate() / 7)} ${d.toLocaleDateString('en', { month: 'short' })}`,
          applied: 0,
          hired: 0,
          rejected: 0,
        };
      }
      trendBuckets[key].applied++;
      if (a.state === 'HIRED') trendBuckets[key].hired++;
      if (a.state === 'REJECTED') trendBuckets[key].rejected++;
    });
    const trendData = Object.entries(trendBuckets)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([, v]) => v);

    // Prev period comparison
    function pctChange(current: number, prev: number): number | null {
      if (prev === 0) return current > 0 ? 100 : null;
      return ((current - prev) / prev) * 100;
    }
    const prevHired = prevApps.filter(a => a.state === 'HIRED').length;
    const appChange = pctChange(periodAppCount, prevAppCount);
    const hiredChange = pctChange(hiredCount, prevHired);

    // TTH semaphore
    function semaphore(value: number | null, target: number): 'green' | 'yellow' | 'red' | null {
      if (value === null) return null;
      if (value <= target) return 'green';
      if (value <= target * 1.2) return 'yellow';
      return 'red';
    }

    const pipelineStatus: PipelineStatusCounts = computePipelineStatusCounts(
      periodApps,
      hasProcessingCredits,
    );
    const funnelOutcomes = {
      rejected: pipelineStatus.REJECTED,
      withdrawn: pipelineStatus.WITHDRAWN,
      offerDeclined: pipelineStatus.OFFER_DECLINED,
    };

    return {
      openPositions,
      activePostings,
      periodAppCount,
      awaitingDecision,
      processing,
      medianTTH,
      medianTTF,
      oar,
      agingPositions,
      pipelineErrors,
      funnelWithRates,
      applicantToInterview,
      interviewToHire,
      applicantToHire,
      applicantToReject,
      channelData,
      topPositions,
      timeInStates,
      trendData,
      appChange,
      hiredChange,
      tthSemaphore: semaphore(medianTTH, TARGET_TTH_DAYS),
      ttfSemaphore: semaphore(medianTTF, TARGET_TTF_DAYS),
      pipelineStatus,
      funnelOutcomes,
    };
  }, [applications, positions, postings, period, hasProcessingCredits]);
}
